import { Colour, HexPalette } from '@xemplo/colours';

import type {
  BgColorFilterFn,
  GetRandomPaletteColorOptions,
} from './clarity-colors.types';

export const CLARITY_BG_COLORS = [
  Colour.Clarity.Blue.Hard,
  Colour.Clarity.Purple.Hard,
  Colour.Clarity.Red.Hard,
  Colour.Clarity.Orange.Hard,
  Colour.Clarity.Green.Hard,
  Colour.Clarity.Grey.Medium,
] as const;

function pickRandomItem<T>(items: readonly T[]) {
  // eslint-disable-next-line sonarjs/pseudo-random
  return items[Math.floor(Math.random() * items.length)];
}

/**
 * FNV-1a 32-bit hash, returned as an unsigned integer.
 *
 * Pure integer arithmetic with `>>> 0` to keep the result unsigned, so the
 * same string yields the same number in any JavaScript runtime. This is what
 * lets separate apps (e.g. expedo-fe and hr-fe) agree on an avatar colour for
 * the same user without sharing any state.
 */
function hashString(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Returns a Clarity background colour that is random-looking but deterministic
 * for a given seed: the same seed always maps to the same colour.
 *
 * Seed on a stable, cross-app identifier (e.g. a persona/user id) so every
 * Xemplo app renders the same colour for the same entity. Pass the raw,
 * unprefixed identifier — consuming apps must seed on the identical value.
 */
export function getSeededColor(seed?: string | number | null): string {
  // Coerce: some callers (e.g. GP) pass a numeric id at runtime.
  const normalised = seed == null ? '' : String(seed).trim().toLowerCase();
  if (!normalised) return CLARITY_BG_COLORS[CLARITY_BG_COLORS.length - 1];
  return CLARITY_BG_COLORS[hashString(normalised) % CLARITY_BG_COLORS.length];
}

export function getRandomClarityBgColor(
  bgColor?: string,
  bgColorFilter?: BgColorFilterFn,
) {
  if (bgColor) return bgColor;

  const filteredColors = bgColorFilter
    ? CLARITY_BG_COLORS.filter((color, index, array) =>
        bgColorFilter(color, index, [...array]),
      )
    : CLARITY_BG_COLORS;

  const colorsToPick = filteredColors.length > 0 ? filteredColors : CLARITY_BG_COLORS;
  return pickRandomItem(colorsToPick);
}

export function findContrastingColor(bgColor?: string) {
  if (!bgColor) return Colour.Clarity.Background.Surface;
  if (CLARITY_BG_COLORS.includes(bgColor as (typeof CLARITY_BG_COLORS)[number])) {
    return Colour.Clarity.Background.Surface;
  }
  return Colour.Clarity.Base.Midnight;
}

function getAvailableColors(paletteEntry: Readonly<Partial<HexPalette>>) {
  return Object.values(paletteEntry).filter((color): color is string => Boolean(color));
}

export function getRandomPaletteColor(options: Readonly<GetRandomPaletteColorOptions>) {
  const { palette, colorFilter } = options;
  const paletteKeys = Object.keys(palette);
  if (paletteKeys.length === 0) return undefined;

  const pick = <T>(items: readonly T[]) => pickRandomItem(items);
  const selectedKey = pick(paletteKeys);
  const selectedPalette = palette[selectedKey];

  const colors = getAvailableColors(selectedPalette);
  if (colors.length === 0) return undefined;

  const filteredColors = colorFilter
    ? colors.filter((color, index, array) => colorFilter(color, index, array))
    : colors;

  const colorsToPick = filteredColors.length > 0 ? filteredColors : colors;
  return pick(colorsToPick);
}
